/**
 * Utility functions for iframe detection and authentication handling
 */

import { initializeConnectSdk, isConnectFullyInitialized, getPersonId, getContactId } from './connectSdk';

/**
 * Check if the current window is running inside an iframe
 */
export const isInIframe = (): boolean => {
  try {
    return window.self !== window.top;
  } catch (e) {
    // If we can't access window.top due to cross-origin restrictions,
    // we're definitely in an iframe
    return true;
  }
};

/**
 * Check if the iframe is hosted within AWS Connect
 */
export const isConnectApp = (): boolean => {
  try {
    const parentUrl = document.referrer || window.top?.location.href;
    return parentUrl ? parentUrl.includes('.my.connect.aws') : false;
  } catch (e) {
    // Fallback: check referrer header
    return document.referrer.includes('.my.connect.aws');
  }
};

/**
 * Auth popup geometry.
 *
 * The flow is a silent redirect chain that closes itself, so there is
 * nothing in this window for a rep to read. We request the smallest window
 * we can and park it in the bottom-right corner: reps were instinctively
 * closing a large centered window before the redirects finished.
 *
 * Two things JS cannot do here, both deliberate browser protections against
 * windows that hide themselves:
 *   - Place the popup partially offscreen. Browsers clamp it onscreen.
 *   - Force an arbitrarily small size. The browser and OS impose a minimum
 *     (it varies by platform, and the window chrome alone sets a floor), so
 *     the request below is a target, not a guarantee. Nothing depends on
 *     the resulting size.
 */
const AUTH_POPUP_WIDTH = 100;
const AUTH_POPUP_HEIGHT = 100;
const AUTH_POPUP_SCREEN_MARGIN = 24;

/**
 * How often we check whether the popup finished or was closed. The popup is
 * visible for as long as this loop takes to notice completion, so a short
 * interval directly shrinks the window in which a rep can react to it.
 */
const AUTH_POPUP_POLL_MS = 250;

/**
 * If the popup is still open this long after opening, the flow is not the
 * usual silent redirect — something is asking the rep for input (session
 * fully expired, re-auth challenge, consent screen). A 100x100 window is
 * unusable for that, so we grow it and bring it into view. Set comfortably
 * above a normal redirect chain so the happy path never triggers it.
 */
const AUTH_POPUP_ESCALATE_MS = 4000;
const AUTH_POPUP_ESCALATED_WIDTH = 480;
const AUTH_POPUP_ESCALATED_HEIGHT = 620;

/** Offset of the display the browser is currently on, within the desktop. */
const currentScreenOffset = (): { left: number; top: number } => {
  // availLeft / availTop are non-standard but broadly supported, and are how
  // we learn the current display's origin. Absent values mean 0, which is
  // correct for a single-monitor setup.
  const screenWithOffsets = window.screen as Screen & { availLeft?: number; availTop?: number };
  return {
    left: screenWithOffsets.availLeft ?? 0,
    top: screenWithOffsets.availTop ?? 0,
  };
};

/**
 * `window.open` feature string placing the popup at the bottom-right of the
 * display the browser is currently on.
 *
 * Uses `window.screen`, which describes the current display rather than the
 * primary one — positioning from a single desktop origin would fling the
 * popup onto monitor 1 for anyone on multiple screens, which is more
 * disruptive than the centered popup this replaces.
 */
export const buildAuthPopupFeatures = (): string => {
  const { left: screenLeft, top: screenTop } = currentScreenOffset();
  const availWidth = window.screen.availWidth || AUTH_POPUP_WIDTH;
  const availHeight = window.screen.availHeight || AUTH_POPUP_HEIGHT;

  const left = Math.round(
    screenLeft + Math.max(0, availWidth - AUTH_POPUP_WIDTH - AUTH_POPUP_SCREEN_MARGIN),
  );
  const top = Math.round(
    screenTop + Math.max(0, availHeight - AUTH_POPUP_HEIGHT - AUTH_POPUP_SCREEN_MARGIN),
  );

  return [
    `width=${AUTH_POPUP_WIDTH}`,
    `height=${AUTH_POPUP_HEIGHT}`,
    `left=${left}`,
    `top=${top}`,
    'scrollbars=yes',
    'resizable=yes',
  ].join(',');
};

/**
 * Grow a stalled popup to a usable size and centre it so a rep can complete
 * an interactive auth step. Best-effort: sizing calls on a popup are subject
 * to browser policy, and failing to resize must not break the auth flow.
 */
const escalateAuthPopup = (popup: Window): void => {
  try {
    const { left: screenLeft, top: screenTop } = currentScreenOffset();
    const availWidth = window.screen.availWidth || AUTH_POPUP_ESCALATED_WIDTH;
    const availHeight = window.screen.availHeight || AUTH_POPUP_ESCALATED_HEIGHT;
    popup.resizeTo(AUTH_POPUP_ESCALATED_WIDTH, AUTH_POPUP_ESCALATED_HEIGHT);
    popup.moveTo(
      Math.round(screenLeft + Math.max(0, (availWidth - AUTH_POPUP_ESCALATED_WIDTH) / 2)),
      Math.round(screenTop + Math.max(0, (availHeight - AUTH_POPUP_ESCALATED_HEIGHT) / 2)),
    );
    popup.focus();
  } catch (e) {
    console.warn('[auth] could not resize auth popup:', e);
  }
};

/**
 * Raised when the popup closed before the auth flow completed — almost
 * always a rep reflexively dismissing it. Distinct from a genuine auth
 * failure because it is recoverable by simply trying again, and the UI
 * should say so rather than dead-ending.
 */
export class AuthPopupClosedError extends Error {
  constructor() {
    super('Authentication popup closed before completing');
    this.name = 'AuthPopupClosedError';
  }
}

/**
 * Options for {@link runAuthPopup}.
 */
export interface RunAuthPopupOptions {
  /** URL to open in the popup. */
  url: string;
  /**
   * Given the popup's same-origin pathname, return true when auth has
   * completed. Only called when the pathname is readable (i.e. the popup is
   * back on our origin); cross-origin reads throw and are ignored.
   */
  isSuccessPath: (pathname: string) => boolean;
}

/**
 * Open an auth popup and drive it to completion.
 *
 * This is the single implementation behind every auth popup — the initial
 * sign-in and the cookie-clear retry both call it. Keeping one copy is
 * deliberate: the escalation timer, the premature-close handling, the
 * timeout, and the success detection are subtle and were previously
 * duplicated, which let the retry flow silently drift out of sync (missing
 * escalation, mis-handling an early close). Callers supply only what
 * actually differs: the URL and how to recognize the success redirect.
 *
 * Resolves when auth completes. Rejects with {@link AuthPopupClosedError}
 * when the popup closes first (recoverable — the rep just closed it), or a
 * generic Error when it can't be opened or times out.
 */
export const runAuthPopup = ({ url, isSuccessPath }: RunAuthPopupOptions): Promise<void> => {
  return new Promise((resolve, reject) => {
    const popup = window.open(url, 'auth-popup', buildAuthPopupFeatures());

    if (!popup) {
      reject(new Error('Failed to open popup - popup blocker may be active'));
      return;
    }

    let authSucceeded = false;

    // Some login pages post 'auth-success' back to the opener; honor it as a
    // fast path. Harmless for flows whose page never posts it.
    const messageListener = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data === 'auth-success') {
        authSucceeded = true;
        cleanup();
        popup.close();
        resolve();
      }
    };

    // Still open well past a normal redirect chain => the rep has to
    // interact with it (fully expired session, re-auth challenge), so grow
    // the tiny window into something usable.
    const escalateTimer = setTimeout(() => {
      if (!popup.closed && !authSucceeded) {
        escalateAuthPopup(popup);
      }
    }, AUTH_POPUP_ESCALATE_MS);

    const checkClosed = setInterval(() => {
      if (popup.closed) {
        cleanup();
        if (authSucceeded) {
          resolve();
        } else {
          reject(new AuthPopupClosedError());
        }
        return;
      }

      try {
        if (isSuccessPath(popup.location.pathname)) {
          authSucceeded = true;
          cleanup();
          popup.close();
          resolve();
        }
      } catch (e) {
        // Cross-origin error is expected while the popup is on the IdP.
      }
    }, AUTH_POPUP_POLL_MS);

    // Cleanup function
    const cleanup = () => {
      clearInterval(checkClosed);
      clearTimeout(escalateTimer);
      clearTimeout(timeout);
      window.removeEventListener('message', messageListener);
    };

    // Timeout after 5 minutes
    const timeout = setTimeout(() => {
      cleanup();
      if (!popup.closed) {
        popup.close();
      }
      reject(new Error('Authentication timeout'));
    }, 300000);

    window.addEventListener('message', messageListener);
  });
};

/**
 * Open the initial authentication popup. Auth is complete once the popup
 * lands back on the app ('/' or '/chat').
 */
export const openAuthPopup = (): Promise<void> =>
  runAuthPopup({
    url: '/api/auth/login?popup=true',
    isSuccessPath: (pathname) => pathname === '/' || pathname.includes('/chat'),
  });

/**
 * Clear cookies then re-run the auth flow. Used on retry when the first
 * attempt failed — the cookieClear endpoint redirects through login and
 * lands on '/' or the OAuth response path.
 */
export const retryAuthPopup = (): Promise<void> =>
  runAuthPopup({
    url: '/api/auth/cookieClear',
    isSuccessPath: (pathname) =>
      pathname === '/' || pathname.includes('/oauth2/idpresponse'),
  });

/**
 * Initialize Connect SDK if running in Connect environment
 * Waits for full initialization including contact attributes
 */
export const initializeConnectIfNeeded = async (): Promise<void> => {
  if (!isInIframe() || !isConnectApp()) {
    return;
  }

  try {
    console.log('Initializing Connect SDK...');
    await initializeConnectSdk();
    console.log('Connect SDK fully initialized with contact data');
  } catch (error) {
    console.warn('Failed to fully initialize Connect SDK:', error);
    throw error;
  }
};